/**
 * What the hotel guide's room search form shares between its two shapes — the bar above the results
 * (`RoomSearchBar`, its pickers in popovers) and the open form on an empty tab (`RoomSearchPanels`, its
 * pickers on screen): how a stay reads, the per-child age slots, and the form's surfaces.
 */
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { format, parseISO } from 'date-fns';
import { useState } from 'react';

import { cn, pluralize } from '@elara/client-core/lib/utils';

/** What both shapes of the form take: the store's form, its setters, and the search. */
export interface RoomSearchFormProps {
  /** Check-in, YYYY-MM-DD. */
  startDate?: string;
  /** Check-out, YYYY-MM-DD. */
  endDate?: string;
  /** The party; the search offers rooms that take everyone, or two of one room. */
  occupancy: Occupancy;
  /** Changes when the form is pre-filled afresh, so the per-child age slots restart from `occupancy`. */
  prefillKey: string;
  onDatesChange: (startDate: string | undefined, endDate: string | undefined) => void;
  onOccupancyChange: (occupancy: Occupancy) => void;
  onSearch: () => void;
  canSearch: boolean;
  isSearching: boolean;
  /** Locks the dates and guests, not the search button: that follows `canSearch`. */
  disabled?: boolean;
}

/** Below this width there is no room for two months side by side. */
export const TWO_MONTHS_MIN_WIDTH = 560;

/**
 * The form's raised surface: lifted by a shadow, not outlined. The bar wears one around all its
 * segments, the open form one per panel.
 */
export const FORM_SURFACE = 'rounded-md bg-background shadow-sm';

/** A surface holding controls that open panels: a focus-coloured ring takes the outline's part while one has focus or its panel is open. */
export const FIELD_SURFACE = cn(
  FORM_SURFACE,
  'transition-shadow',
  'focus-within:ring-1 focus-within:ring-input-focus has-[[data-state=open]]:ring-1 has-[[data-state=open]]:ring-input-focus',
);

/** "17 Nov – 21 Nov · 4 nights", or undefined with no check-in yet. */
export function describeStay(startDate: string | undefined, endDate: string | undefined): string | undefined {
  if (!startDate) return;
  const start = parseISO(startDate);
  if (!endDate) return `${format(start, 'd MMM')} – Check-out?`;
  const end = parseISO(endDate);
  const nights = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM')} · ${nights} ${pluralize(nights, 'night')}`;
}

function ageSlotsOf(occupancy: Occupancy): string[] {
  return Array.from({ length: occupancy.children ?? 0 }, (_, i) => {
    const age = occupancy.childrenAges?.[i];
    return age === undefined ? '' : String(age);
  });
}

export interface ChildAgeSlots {
  /** One slot per child, '' until its age is chosen. */
  ages: string[];
  setAdults: (adults: number) => void;
  setChildren: (count: number) => void;
  setChildAge: (index: number, age: string) => void;
}

/**
 * The party as the guests picker edits it. Each child's age is a slot of its own, '' until chosen, so an
 * unset age keeps its place in the list: the store holds only the ages given, in order. Held by the form
 * rather than the picker, which unmounts with its popover. The slots restart from `occupancy` whenever
 * `prefillKey` changes (a fresh pre-fill).
 */
export function useChildAgeSlots(
  occupancy: Occupancy,
  prefillKey: string,
  onOccupancyChange: (occupancy: Occupancy) => void,
): ChildAgeSlots {
  const [ages, setAges] = useState(() => ageSlotsOf(occupancy));
  const [agesPrefillKey, setAgesPrefillKey] = useState(prefillKey);
  if (agesPrefillKey !== prefillKey) {
    setAgesPrefillKey(prefillKey);
    setAges(ageSlotsOf(occupancy));
  }

  function emit(adults: number, slots: string[]) {
    setAges(slots);
    const filled = slots.filter((a) => a !== '').map(Number);
    onOccupancyChange({ adults, ...(slots.length > 0 ? { children: slots.length, childrenAges: filled } : {}) });
  }

  return {
    ages,
    setAdults: (adults) => emit(adults, ages),
    setChildren: (count) => {
      const next = ages.slice(0, count);
      while (next.length < count) next.push('');
      emit(occupancy.adults, next);
    },
    setChildAge: (index, age) =>
      emit(
        occupancy.adults,
        ages.map((a, j) => (j === index ? age : a)),
      ),
  };
}
